import { describeHelpers, Labels } from "../../../../../features/data-types"
import { RoutePath, useRoutesHooks } from "../../../../../features/routes"
import { Action, DetailFieldProps, DetailPanel, FieldType, FieldValue, ModalId, Tab, UIPermission, useGetCrumbs, useUIActions } from "../../../../../features/ui"
import { TableName } from "../../../../../tables"
import { useSystemStateHooks } from "../../../../system"
import { Roadmap } from "../../../state/types"
import { useEffect, useMemo, useState } from "react"
import { useRoadmapHooks } from "../../../hooks/roadmapHooks"
import { PreparationStatus } from "../../../../common"
import { RouteDetail } from "../route-detail/routeDetail"
import { Address } from "../../../../../features/address"
import { useLocation, useNavigate } from "react-router-dom"
import { Delivery, DeliveryDataList, DeliveryOrder, DownloadDeliveryNoteModal, DownloadDeliveryNotesModal, UploadDeliveryNoteModal, useDeliveryHooks } from "../../../../deliveries"
import { Operation } from "../../../../operations"

type Props = {
    shipments: Delivery[]
    shipmentOrders: DeliveryOrder[]
    operations: Operation[]
    routeAddressesData: Map<string, Address>
}

export const RoadmapDetail = (props: Props) => {
    const { shipmentOrders, operations, routeAddressesData } = props
    const { dataType } = useRoutesHooks().parseUrl()
    const roadmap = dataType! as Roadmap

    const [shipments, setShipments] = useState<Delivery[]>(props.shipments)
    const [isCompletable, setIsCompletable] = useState(false)
    const [currentShipment, setCurrentShipment] = useState<Delivery>()
    
    const { describeCode } = describeHelpers
    const { userHasPermission } = useSystemStateHooks()
    const readOnly = !userHasPermission(UIPermission.SAVE_ROADMAPS)
    const hasDeliveryPermission = userHasPermission(UIPermission.SAVE_SALE_DELIVERIES)
    const { isRoadmapCompletable } = useRoadmapHooks()
    const { refreshDeliveries } = useDeliveryHooks()
    const navigate = useNavigate()
    const locationFrom = useLocation().state?.from
    const fromShipmentPreparation = locationFrom === RoutePath.SHIPMENT_PREPARATION

    const { openModal } = useUIActions()

    useEffect(() => {
        isRoadmapCompletable(roadmap).then(setIsCompletable)
    }, [roadmap.id])

    const title = useMemo(() => `Hoja de Ruta ${describeCode(roadmap)}`, [roadmap])
    const crumbs = useGetCrumbs(
        fromShipmentPreparation ? 'Preparación de Envíos' : 'Hojas de Ruta',
        locationFrom || RoutePath.ROADMAPS,
        `${fromShipmentPreparation ? 'Hoja de Ruta ' : ''}${describeCode(roadmap)}`
    )

    const findShipmentOrder = (shipment?: Delivery) => shipmentOrders.find(order => order.id === shipment?.orderId)

    const fields: DetailFieldProps[] = [
        {
            name: 'code',
            type: FieldType.NUMBER,
            label: '#'
        },
        {
            name: 'date',
            type: FieldType.DATE,
            label: 'Fecha'
        },
        {
            name: 'driverId',
            type: FieldType.TABLE,
            table: TableName.DRIVERS,
            label: 'Conductor'
        },
        {
            name: 'vehicleId',
            type: FieldType.TABLE,
            table: TableName.VEHICLES,
            label: 'Vehículo'
        },
        {
            name: 'preparationStatus',
            type: FieldType.TEXT,
            label: 'Estado de Preparación',
            render: (value?: FieldValue) => <>{Labels.preparationStatusLabel(value as PreparationStatus)}</>
        },
        {
            name: 'map',
            type: FieldType.IMAGE,
            render: () => <RouteDetail addresses={routeAddressesData} />
        }
    ]

    const onDownloadDeliveryNotes = () => openModal(ModalId.DOWNLOAD_FILES)

    const onCompleteDelivery = () => navigate(RoutePath.COMPLETE_DELIVERY)

    const onPrepareTruck = () => navigate(RoutePath.PREPARE_TRUCK)

    const actions: Action[] = useMemo(() => {
        const actionList: Action[] = []

        const hasDeliveries = shipments.length > 0
        const isReady = roadmap.preparationStatus === PreparationStatus.READY

        if (hasDeliveries) {
            actionList.push({ label: 'Remitos', icon: 'download', kind: 'tertiary', onClick: onDownloadDeliveryNotes })
        }
        if (fromShipmentPreparation && roadmap.preparationStatus === PreparationStatus.PENDING) {
            actionList.push({ label: 'Preparar Camión', icon: 'truck', onClick: onPrepareTruck })
        }
        if (hasDeliveryPermission && isReady && isCompletable) {
            actionList.push({ label: 'Completar Envío', icon: 'bag-check-fill', onClick: onCompleteDelivery })
        }

        return actionList
    }, [roadmap.preparationStatus, hasDeliveryPermission, shipments, isCompletable])

    const tabs = useMemo(() => {
        const tabList: Tab[] = [
            {
                id: 'shipments',
                title: 'Envíos',
                content: <DeliveryDataList
                    deliveries={shipments}
                    deliveryOrders={shipmentOrders}
                    operations={operations}
                    defaultExpanded
                    onChangeCurrentDelivery={setCurrentShipment}
                />
            }
        ]
        
        return tabList
    }, [shipments])

    const onUploadDeliveryNote = async () => {
        const refreshedDeliveries = await refreshDeliveries(shipments)
        setShipments(refreshedDeliveries)
    }

    return (
        <>
            <DetailPanel
                tableName={TableName.ROADMAPS}
                title={title}
                crumbs={crumbs}
                fields={fields}
                data={roadmap}
                icon="map"
                readOnly={readOnly}
                actions={actions}
                tabs={tabs}
            />
            <DownloadDeliveryNoteModal
                delivery={currentShipment}
                deliveryOrder={findShipmentOrder(currentShipment)}
            />
            <DownloadDeliveryNotesModal
                deliveries={shipments}
                deliveryOrders={shipmentOrders}
            />
            <UploadDeliveryNoteModal
                delivery={currentShipment}
                deliveryOrder={findShipmentOrder(currentShipment)}
                onUpload={onUploadDeliveryNote}
            />
        </>
    )
}
